import { Typography } from '@mui/material'
import { useFormik } from 'formik'
import { Suspense } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as yup from 'yup'

import EditableTitle from '~/components/editableTitle'
import LoadingPaperSkeleton from '~/components/loadingPaperSkeleton'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import useSetSettingsSteps from '~/features/agents/hooks/useSetSettingsSteps'
import useUpdateAgent from '~/features/agents/hooks/useUpdateAgent'
import { EAgentKeys } from '~/features/agents/models'
import { EClientPath } from '~/models/api'

type TProps = {
  children?: JSX.Element | null
}

function SettingsLayout({ children = null }: TProps) {
  const { pathname } = useLocation()
  const subpageSpecialTitles = [
    { path: EClientPath.settingsPhoneText, title: 'Phone & Text' },
  ]
  const specialSubtitle = subpageSpecialTitles.find((specialTitle) =>
    pathname.startsWith(specialTitle.path),
  )
  const subpageTitle =
    specialSubtitle?.title ||
    pathname.replace('/', '').split('/')[1]?.replaceAll(/-/g, ' ')

  const isLoadingSetSettings = useSetSettingsSteps()
  const { selectedAgent: agent, isLoading: isLoadingAgent } =
    useGetAgentCentralized()
  const { mutate: updateAgent } = useUpdateAgent()

  const validationSchema = yup.object({
    [EAgentKeys.agentReferenceName]: yup
      .string()
      .required('Assistant name cannot be empty.'),
  })

  const formik = useFormik({
    initialValues: {
      [EAgentKeys.agentReferenceName]: agent.agentReferenceName || '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      updateAgent(
        {
          agentId: agent.id,
          payload: {
            [EAgentKeys.agentReferenceName]:
              values[EAgentKeys.agentReferenceName],
          },
        },
        {
          onError: () => {
            toast.error('')
          },
        },
      )
    },
  })

  if (!agent?.id && !isLoadingAgent) {
    return null
  }

  return (
    <Suspense
      fallback={
        <>
          <LoadingPaperSkeleton sx={{ height: '2.9rem', mb: 5 }} />
          <LoadingPaperSkeleton sx={{ height: '60vh' }} />
        </>
      }
    >
      {isLoadingAgent ? (
        <LoadingPaperSkeleton sx={{ height: '2.9rem' }} />
      ) : (
        <EditableTitle
          name={EAgentKeys.agentReferenceName}
          ariaLabel="Assistant name"
          value={formik.values.agentReferenceName}
          onChange={formik.handleChange}
          error={
            formik.touched.agentReferenceName &&
            Boolean(formik.errors.agentReferenceName)
          }
          helperText={
            formik.touched.agentReferenceName
              ? formik.errors.agentReferenceName
              : ''
          }
          onSubmit={formik.handleSubmit}
        />
      )}
      <Typography variant="h6" component="h2" textTransform="capitalize">
        {subpageTitle}
      </Typography>

      {isLoadingSetSettings || isLoadingAgent ? (
        <LoadingPaperSkeleton sx={{ height: '60vh' }} />
      ) : (
        children
      )}
    </Suspense>
  )
}

export default SettingsLayout
